import { motion } from 'framer-motion';
import { Calendar, ExternalLink, Shield, Clock, CreditCard, Crown, Users, ChefHat, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const SQUARE_BOOKING_URL = "https://book.squareup.com/appointments/6n6e13dyx9n9yv/location/GVCMSN6NG46K9/services";

const CHEF_JAYLON = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/n5u32e78_20200430_145615%20%281%29.jpg";
const STEAK_LOBSTER_PLATE = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/6xq6raql_copilot_image_1770938235981.jpeg";
const BENTO_SPREAD = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/jt37pe3s_image_1770939759278.jpeg";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export const BookingPage = () => {
  const services = [
    {
      icon: <ChefHat className="w-8 h-8" />,
      title: "Royal Delights Hibachi Experience",
      description: "Our chefs bring their favorite Hibachi grill and put on a show for your guests. The most fun and engaging experience for any occasion.",
      price: "$200 Booking Fee",
      duration: "2+ hours",
      note: "Booking fee + per person cost + $125 chef fee"
    },
    {
      icon: <Crown className="w-8 h-8" />,
      title: "Royal Sushi Catering Experience",
      description: "Embark on a culinary journey with exquisite sushi creations. A 5-course dining experience with a personal Sushi Chef.",
      price: "$350",
      duration: "2 hr 30 min",
      note: "Includes personal sushi chef"
    }
  ];

  const steps = [
    { num: "1", text: "Click the booking button below" },
    { num: "2", text: "Select your experience (Hibachi or Sushi)" },
    { num: "3", text: "Choose your date and time" },
    { num: "4", text: "Complete secure payment via Square" }
  ];

  const included = [
    "Professional chef at your location",
    "All cooking equipment provided",
    "Setup and cleanup included",
    "Fresh, premium ingredients",
    "Entertainment and showmanship",
    "Personalized menu consultation"
  ];

  return (
    <div data-testid="booking-page" className="min-h-screen bg-[#050505]">
      
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={STEAK_LOBSTER_PLATE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p variants={fadeInUp} className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">
              Reserve Your Date
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F0] mb-6">
              Book Your <span className="italic text-[#D4AF37]">Royal Experience</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA] mb-10">
              Select your experience, choose your date, and let us bring the celebration to you.
            </motion.p>

            {/* Main CTA */}
            <motion.div variants={fadeInUp}>
              <a href={SQUARE_BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="main-booking-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-10 py-6 text-lg transition-all duration-300 hover:scale-105 shadow-[0_0_40px_-10px_rgba(212,175,55,0.6)]"
                >
                  <Calendar className="mr-3 w-6 h-6" />
                  Book Now on Square
                  <ExternalLink className="ml-3 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-[#121212]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-heading text-3xl font-bold text-[#F5F5F0] text-center mb-12">
              Choose Your Experience
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-[#050505] border border-white/10 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition-all duration-500 group"
                >
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                    <span className="text-[#D4AF37]">{service.icon}</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-[#F5F5F0] mb-3">{service.title}</h3>
                  <p className="font-body text-[#A1A1AA] mb-6 leading-relaxed">{service.description}</p>
                  
                  <div className="flex items-center justify-between pt-6 border-t border-white/10">
                    <div>
                      <p className="font-heading text-2xl text-[#D4AF37]">{service.price}</p>
                      <p className="font-body text-xs text-[#A1A1AA]">{service.duration}</p>
                    </div>
                  </div>
                  <p className="font-body text-xs text-[#A1A1AA] mt-4 italic">{service.note}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-heading text-3xl font-bold text-[#F5F5F0] text-center mb-12">
              How to Book
            </motion.h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center mx-auto mb-4">
                    <span className="font-heading text-xl font-bold text-black">{step.num}</span>
                  </div>
                  <p className="font-body text-sm text-[#A1A1AA]">{step.text}</p>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div variants={fadeInUp} className="text-center mt-12">
              <a href={SQUARE_BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="secondary-booking-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-8 py-4 transition-all duration-300 hover:scale-105"
                >
                  Start Booking
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-[#121212]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img src={CHEF_JAYLON} alt="Chef Jaylon" className="w-full h-full object-cover object-top" />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="font-heading text-3xl font-bold text-[#F5F5F0] mb-8">
                What's Included
              </motion.h2>

              <div className="space-y-4 mb-10">
                {included.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="flex items-center gap-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <span className="font-body text-[#F5F5F0]">{item}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeInUp}>
                <a href={SQUARE_BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-8 py-4 transition-all duration-300 hover:scale-105"
                  >
                    Reserve Your Date
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Important Info */}
      <section className="py-16 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="bg-[#121212] border border-[#D4AF37]/30 rounded-2xl p-8"
          >
            <motion.h3 variants={fadeInUp} className="font-heading text-xl font-bold text-[#D4AF37] mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Important Booking Information
            </motion.h3>
            <motion.ul variants={fadeInUp} className="space-y-3 font-body text-[#A1A1AA]">
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF37]">•</span>
                <span>$200 booking fee is required to reserve your date (non-refundable)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF37]">•</span>
                <span>Minimum invoice of $750 per booking (includes chef fee and booking fee)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF37]">•</span>
                <span>Chef fee of $125 applies to all Hibachi experiences</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#D4AF37]">•</span>
                <span>We'll contact you within 24 hours to finalize menu and details</span>
              </li>
            </motion.ul>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#121212]">
        <motion.div 
          className="max-w-4xl mx-auto px-6 lg:px-8 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0] mb-6"
          >
            Ready to Experience <span className="italic text-[#D4AF37]">Royalty</span>?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-[#A1A1AA] mb-10"
          >
            Join the Royal Family and create unforgettable memories with our chef-driven experiences.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <a href={SQUARE_BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="final-booking-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-12 py-6 text-lg transition-all duration-300 hover:scale-105 shadow-[0_0_40px_-10px_rgba(212,175,55,0.6)]"
              >
                <Crown className="mr-3 w-6 h-6" />
                Book Your Royal Experience
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default BookingPage;
